// Shared activity / note composer — the ONE place activities & notes are
// created across Talmia (Customer 360, Deal Pipeline, Quote Builder, Quotes).
// Keyed to a CRM person; optionally uploads files to that person's Attachments.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Activity, StickyNote, X, CalendarDays, Users2, UploadCloud, Paperclip,
  Trash2, Loader2, BellRing, CalendarPlus, Info,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { uploadMedia, MAX_MEDIA_BYTES } from "../../lib/mediaUpload";
import { ACTIVITY_GROUPS, REQUIRE_DUE, TYPE_FIELDS, autoTitle, metaFor, activityToneCls } from "./activityMeta";
import CustomerBrief from "./CustomerBrief";
import TypeDetails, { DueChips, isoIn } from "./TypeDetails";
import CRMPerson360 from "../../pages/crm/CRMPerson360";
import VoiceNoteButton from "../../pages/quotes/panels/VoiceNoteButton";
import { AiTag, AiBar, AiFillButton, AI_RING, useAiSuggest } from "./AiSuggest";
import { ThemeToggle, useTheme } from "../../context/ThemeContext";

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ActivityLogModal({
  personId,
  kind = "activity",
  onClose,
  onSaved,
  defaultAssigneeName = null,
  contextLabel = null,
  activity = null,
  quoteId = null,
  quoteRef = null,
  draft = null,
}) {
  const { user } = useAuth();
  const isEdit = !!activity;
  const [tab, setTab] = useState(isEdit ? "activity" : (kind === "note" ? "note" : "activity"));
  const isActivity = tab === "activity";
  const lightOn = useTheme().theme === "light";
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [upProgress, setUpProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(isEdit ? {
    activity_type: activity.activity_type || "Call",
    subject: activity.subject || "",
    content: activity.content || "",
    due_date: activity.due_date || "",
    priority: activity.priority || "medium",
    assignee_id: activity.assignee_id || "",
    status: activity.status || (activity.completed ? "done" : "open"),
  } : {
    activity_type: draft?.activity_type || "Call", subject: draft?.subject || "", content: draft?.content || "", due_date: draft?.due_date || "",
    priority: draft?.priority || "medium", assignee_id: "", status: "open",
  });
  const firstRef = useRef(null);
  const [details, setDetails] = useState(() => {
    if (activity?.details) return activity.details;
    const pre = {};
    if (draft?.activity_type) (TYPE_FIELDS[draft.activity_type]?.fields || []).forEach((f) => {
      if (f.prefill === "quoteRef" && quoteRef) pre[f.key] = quoteRef;
    });
    return pre;
  });
  const [statusTouched, setStatusTouched] = useState(isEdit);
  const [follow, setFollow] = useState({ on: false, due: "", touched: false });
  const [notify, setNotify] = useState(true);
  const [brief, setBrief] = useState(null);
  const [show360, setShow360] = useState(false);
  const rule = TYPE_FIELDS[form.activity_type] || {};
  const needsDue = form.status === "open" && (REQUIRE_DUE.has(form.activity_type) || (typeof rule.due === "function" ? rule.due(details) : !!rule.due));
  const title = form.subject.trim() || autoTitle(form.activity_type, details);

  useEffect(() => { if (personId) api.get(`/crm/persons/${personId}/brief`).then(({ data }) => setBrief(data)).catch(() => setBrief({ name: contextLabel || "Customer" })); }, [personId]); // eslint-disable-line react-hooks/exhaustive-deps

  const prefillFor = (t) => {
    const pre = {};
    (TYPE_FIELDS[t]?.fields || []).forEach((f) => {
      if (f.prefill === "address" && brief?.address) pre[f.key] = brief.address;
      if (f.prefill === "quoteRef" && quoteRef) pre[f.key] = quoteRef;
    });
    return pre;
  };
  const ai = useAiSuggest({ form, details, follow, statusTouched }, { setForm, setDetails, setFollow, setStatusTouched },
    { customer: brief?.name || contextLabel, quoteRef, quoteId, prefill: prefillFor });
  const aiT = (k) => ai.has(k) && <AiTag k={k} onAccept={ai.accept} onReject={ai.reject} />;
  const aiR = (k) => (ai.has(k) ? AI_RING : "");
  const onVoice = async ({ raw, clean }) => {
    ai.markUsed();
    if (!isActivity) { setForm((f) => ({ ...f, content: [f.content, clean || raw].filter(Boolean).join("\n") })); toast.success("Transcribed — check it, then save"); return; }
    const text = [form.content, raw || clean].filter(Boolean).join("\n");
    setForm((f) => ({ ...f, content: text }));
    await ai.suggest(text);
  };

  const pickType = (t) => {
    Object.keys(ai.pending).filter((k) => k === "type" || k.startsWith("d:")).forEach(ai.accept);
    setDetails(prefillFor(t));
    setFollow({ on: false, due: "", touched: false });
    setForm((f) => ({ ...f, activity_type: t }));
  };

  // Smart defaults: status follows the type/outcome (until the user picks), follow-up suggested on no-answer etc.
  useEffect(() => {
    if (isEdit) return;
    if (!statusTouched) setForm((f) => ({ ...f, status: rule.done?.(details) ? "done" : "open" }));
    const days = rule.follow?.(details);
    if (!follow.touched) setFollow(days ? { on: true, due: isoIn(days), touched: false } : { on: false, due: "", touched: false });
  }, [form.activity_type, details]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (window.innerWidth >= 640) firstRef.current?.focus({ preventScroll: true }); }, [tab]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  // Default assignee: the deal/quote Account Manager if we can match them to a
  // user, otherwise the current user. Only sets while still unassigned.
  useEffect(() => {
    if (form.assignee_id || isEdit) return;
    if (defaultAssigneeName && !users.length) return; // wait for the user list so the AM can be matched
    let pick = "";
    if (defaultAssigneeName) {
      const want = String(defaultAssigneeName).trim().toLowerCase();
      const m = users.find(
        (u) => (u.name || "").toLowerCase() === want || (u.email || "").toLowerCase() === want,
      );
      if (m) pick = m.id;
    }
    if (!pick && user?.id) pick = user.id;
    if (pick) setForm((f) => (f.assignee_id ? f : { ...f, assignee_id: pick }));
  }, [users, user, defaultAssigneeName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const ok = [];
    for (const f of incoming) {
      if (f.size > MAX_MEDIA_BYTES) { toast.error(`${f.name} is too large (20 MB max)`); continue; }
      ok.push(f);
    }
    if (ok.length) setFiles((prev) => [...prev, ...ok]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const onPaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (pasted.length) { e.preventDefault(); addFiles(pasted); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (isActivity) {
      if (!title.trim()) { setErr("Add a title or pick some details"); return; }
      if (needsDue && !form.due_date) { setErr(`${form.activity_type} needs a due date — it schedules a task.`); return; }
      if (follow.on && !follow.due) { setErr("Pick a date for the follow-up"); return; }
      if (Object.keys(ai.pending).length) { setErr("Accept or reject the violet AI suggestions first — they become yours once accepted."); return; }
    } else if (!form.content.trim()) {
      setErr("Note cannot be empty");
      return;
    }
    setSaving(true);
    try {
      if (isActivity) {
        const assignee = users.find((u) => u.id === form.assignee_id);
        const payload = {
          activity_type: form.activity_type,
          subject: title.trim(),
          content: form.content.trim() || null,
          due_date: form.due_date || null,
          priority: form.priority,
          assignee_id: form.assignee_id || null,
          assignee_name: assignee ? (assignee.name || assignee.email) : null,
          status: form.status,
          completed: form.status === "done",
          details,
          ...(ai.used || draft?.ai_drafted ? { ai_assisted: true } : {}),
        };
        if (isEdit) {
          await api.patch(`/crm/activities/${activity.id}`, payload);
          toast.success("Activity updated");
        } else {
          const { data } = await api.post(`/crm/persons/${personId}/activities`, {
            ...payload, quote_id: quoteId, notify_linked: notify,
            create_task: form.status === "open" && !!form.due_date,
          });
          if (follow.on) {
            await api.post(`/crm/persons/${personId}/activities`, {
              activity_type: "Follow-up", subject: `Follow-up: ${title}`.slice(0, 200), due_date: follow.due, priority: form.priority,
              assignee_id: payload.assignee_id, assignee_name: payload.assignee_name, status: "open", create_task: true,
              details: { reason: form.activity_type }, quote_id: quoteId, notify_linked: false,
            }).catch(() => toast.error("Activity saved, but the follow-up couldn't be booked. Add it from the activity list."));
          }
          toast.success(
            (form.status !== "open" ? "Activity logged" : form.due_date ? "Activity scheduled · task created" : "Activity logged")
            + (follow.on ? " · follow-up booked" : ""),
            data?.notified ? { description: `${data.notified} linked ${data.notified === 1 ? "person" : "people"} notified` } : undefined,
          );
        }
      } else {
        await api.post(`/crm/persons/${personId}/notes`, { content: form.content.trim() });
        toast.success("Note added");
      }
      if (files.length) {
        setUploadingFiles(true);
        const provenance = `${isActivity ? form.activity_type : "Note"}${form.subject ? `: ${form.subject.trim()}` : ""}`;
        let uploaded = 0;
        for (const f of files) {
          try {
            setUpProgress(0);
            await uploadMedia(f, { contextType: "crm", entityId: personId, description: provenance, onProgress: setUpProgress });
            uploaded += 1;
          } catch (upErr) {
            toast.error(`Upload failed: ${f.name}`, { description: upErr?.response?.data?.detail || upErr.message });
          }
        }
        setUploadingFiles(false);
        if (uploaded) toast.success(`${uploaded} file${uploaded === 1 ? "" : "s"} attached`);
      }
      await onSaved?.();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Failed to save");
      setSaving(false);
      setUploadingFiles(false);
    }
  };

  const L = "block text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5";
  const field = "w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 focus:border-purple-400/60 focus:bg-white/[0.05] outline-none text-sm text-white placeholder:text-slate-500";
  const STATUSES = [
    { k: "open", label: "Open / to do", cls: "border-amber-500/60 bg-amber-500/20 text-amber-200" },
    { k: "done", label: "Done", cls: "border-emerald-500/60 bg-emerald-500/20 text-emerald-200" },
    { k: "cancelled", label: "Cancelled", cls: "border-slate-500/60 bg-slate-500/20 text-slate-200" },
  ];
  const modal = (
    <div className={`fixed inset-0 z-[200] flex ${lightOn ? "bg-slate-900/45" : "bg-black/70"} p-3 sm:p-4 ${show360 ? "items-stretch gap-3" : "items-center justify-center"}`}
      onClick={onClose} data-testid="activity-modal" data-talmia-theme={lightOn ? "light" : undefined}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} onPaste={onPaste}
        style={{ backgroundColor: lightOn ? "#FFFFFF" : "#0F1420" }}
        className={`w-full ${show360 ? "max-w-[720px] shrink-0" : "max-w-[760px]"} max-h-full sm:max-h-[94vh] overflow-y-auto rounded-2xl border ${lightOn ? "!border-slate-200" : "border-white/10"} shadow-2xl flex flex-col`}>
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between sticky top-0 z-10"
          style={{ backgroundColor: lightOn ? "#FFFFFF" : "#0F1420" }}>
          <div className="flex items-center gap-1.5" data-testid="activity-modal-tabs">
            <button type="button" onClick={() => setTab("activity")} data-testid="activity-modal-tab-activity"
              className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-lg text-[12px] sm:text-[13px] font-semibold border transition-colors ${isActivity ? "border-purple-500/60 bg-purple-500/20 text-purple-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
              <Activity className="w-4 h-4" /> {isEdit ? "Edit activity" : "Log activity"}
            </button>
            {!isEdit && (
              <button type="button" onClick={() => setTab("note")} data-testid="activity-modal-tab-note"
                className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-lg text-[12px] sm:text-[13px] font-semibold border transition-colors ${!isActivity ? "border-orange-500/60 bg-orange-500/20 text-orange-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
                <StickyNote className="w-4 h-4" /> Customer note
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle className="!w-8 !h-8" />
            <button type="button" onClick={onClose} data-testid="activity-modal-close" aria-label="Close"
              className="w-8 h-8 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="p-5 space-y-4 flex-1">
          <CustomerBrief b={brief} show360={show360} onToggle360={() => setShow360((v) => !v)} />
          {isActivity && <AiBar count={Object.keys(ai.pending).length} onAcceptAll={ai.acceptAll} onRejectAll={ai.rejectAll} />}

          {isActivity ? (<>
            <div data-testid="activity-modal-type-picker" className={ai.has("type") ? `${AI_RING} p-2` : ""}>
              <label className={L}>What happened?{aiT("type")}</label>
              <div className="grid sm:grid-cols-2 gap-2">
                {ACTIVITY_GROUPS.map((g) => (
                  <div key={g.label} className="rounded-lg border border-white/[0.06] p-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{g.label}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.types.map((t) => {
                        const Icon = metaFor(t).icon; const on = form.activity_type === t;
                        return (
                          <button key={t} type="button" onClick={() => pickType(t)} data-testid={`activity-modal-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium border transition-colors ${on ? activityToneCls(t) : "border-white/10 bg-white/[0.03] text-slate-400 hover:text-white"}`}>
                            <Icon className="w-3.5 h-3.5" /> {t}
                          </button>);
                      })}
                    </div>
                  </div>))}
              </div>
            </div>

            <TypeDetails type={form.activity_type} details={details} onChange={setDetails} aiTag={(k) => aiT(`d:${k}`)} aiRing={(k) => aiR(`d:${k}`)} />

            <div>
              <label className={L}>Title <span className="normal-case tracking-normal text-slate-600">(auto-filled from your picks, edit if you like)</span>{aiT("subject")}</label>
              <input ref={firstRef} type="text" value={form.subject} maxLength={200} data-testid="activity-modal-subject"
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder={autoTitle(form.activity_type, details)} className={`${field} ${aiR("subject")}`} />
            </div>
            <div>
              <div className="flex items-end justify-between gap-2 mb-1.5">
                <label className={`${L} !mb-0`}>What was said / agreed{ai.has("content") && <span className="normal-case tracking-normal text-violet-300"> · tidied version</span>}{aiT("content")}</label>
                <div className="flex gap-1.5">
                  <VoiceNoteButton url="/ai/transcribe" onResult={onVoice} testid="activity-voice" label="Speak it" title="Speak what happened — AI types it and suggests the rest for you to approve" />
                  <AiFillButton busy={ai.busy} disabled={!form.content.trim()} onClick={() => { ai.markUsed(); ai.suggest(form.content); }} />
                  {quoteId && (
                    <button type="button" onClick={() => { ai.markUsed(); ai.fromQuoteNotes(); }} disabled={ai.busy} data-testid="activity-from-quote-notes"
                      title="AI drafts this activity from the quote's CW staff notes — lands as violet suggestions for you to approve"
                      className="inline-flex items-center gap-1 px-2.5 h-[30px] rounded-lg border border-violet-400/40 text-violet-200 text-[11px] font-bold hover:bg-violet-500/15 disabled:opacity-40 whitespace-nowrap">
                      From quote notes
                    </button>
                  )}
                </div>
              </div>
              <textarea value={form.content} rows={4} data-testid="activity-modal-content"
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder="Key points, prices discussed, next steps… or press Speak it" className={`${field} resize-y ${aiR("content")}`} />
            </div>

            <div>
              <label className={L}>Status{aiT("status")}</label>
              <div className={`flex gap-1.5 ${aiR("status")}`} data-testid="activity-modal-status-picker">
                {STATUSES.map((st) => (
                  <button key={st.k} type="button" onClick={() => { setStatusTouched(true); setForm((f) => ({ ...f, status: st.k })); }}
                    data-testid={`activity-modal-status-${st.k}`}
                    className={`flex-1 px-2.5 py-2 rounded-md text-[12.5px] font-medium border transition-colors ${form.status === st.k ? st.cls : "border-white/10 bg-white/[0.03] text-slate-400 hover:text-white"}`}>{st.label}</button>))}
              </div>
              <div className="text-slate-500 font-mono text-[10.5px] mt-1">
                {form.status === "open" ? "To do. With a due date it becomes a task for the assignee." : form.status === "done" ? "Logged as done (it already happened)." : "Closed without action."}
                {!statusTouched && !isEdit && " Set automatically from the type; click to change."}
              </div>
            </div>

            {(form.status === "open" || needsDue) && (
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <label className={`${L} flex items-center gap-1.5`}><CalendarDays className="w-3 h-3" /> Due date {needsDue && <span className="text-rose-400 normal-case">* required</span>}{aiT("due_date")}</label>
                  <DueChips value={form.due_date} onChange={(d) => setForm((f) => ({ ...f, due_date: d }))} testid="activity-modal-due-chips" />
                </div>
                <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                  data-testid="activity-modal-due-date" className={`${field} sm:w-44 ${aiR("due_date")}`} />
              </div>
            )}

            {!isEdit && (
              <div className={`rounded-lg border p-3 ${follow.on ? "border-purple-400/40 bg-purple-500/[0.06]" : "border-white/10"} ${aiR("follow")}`} data-testid="activity-followup">
                <label className="flex items-center gap-2 text-[13px] text-white cursor-pointer">
                  <input type="checkbox" checked={follow.on} className="w-4 h-4 accent-purple-400" data-testid="activity-followup-toggle"
                    onChange={(e) => setFollow({ on: e.target.checked, due: follow.due || isoIn(1), touched: true })} />
                  <CalendarPlus className="w-4 h-4 text-purple-300" /> Also book a follow-up
                  {follow.on && !follow.touched && <span className="text-[10.5px] text-purple-300 font-mono">suggested</span>}
                  {aiT("follow")}
                </label>
                {follow.on && <div className="mt-2"><DueChips value={follow.due} onChange={(d) => setFollow({ ...follow, due: d, touched: true })} testid="activity-followup-due" /></div>}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={`${L} flex items-center gap-1.5`}><Users2 className="w-3 h-3" /> Assign to</label>
                <select value={form.assignee_id} onChange={(e) => setForm((f) => ({ ...f, assignee_id: e.target.value }))}
                  data-testid="activity-modal-assignee" className={`${field} bg-slate-900/70`}>
                  <option value="">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}{u.id === user?.id ? " (You)" : ""}</option>)}
                </select>
              </div>
              <div>
                <label className={L}>Priority{aiT("priority")}</label>
                <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  data-testid="activity-modal-priority" className={`${field} bg-slate-900/70 ${aiR("priority")}`}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
            </div>
            {!isEdit && (
              <label className="flex items-center gap-2 text-[12.5px] text-slate-200 cursor-pointer">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="w-4 h-4 accent-teal-400" data-testid="activity-notify" />
                <BellRing className="w-3.5 h-3.5 text-teal-300" /> Notify the AM, assignee{quoteId ? " and quote collaborators" : ""}
              </label>
            )}
          </>) : (
            <div>
              <div className="mb-2 flex items-start gap-2 rounded-lg border border-orange-400/25 bg-orange-500/[0.06] p-2.5 text-[12px] text-orange-100" data-testid="activity-note-where">
                <Info className="w-4 h-4 shrink-0 text-orange-300 mt-0.5" />
                <span className="flex-1">Saved on <b>{brief?.name || "this customer"}</b>'s record (Customer 360 → timeline). {quoteRef ? <>It is <b>not</b> attached to quote {quoteRef}; use CW Staff Notes on the quote for quote-only notes.</> : "Everyone who opens this customer will see it."}</span>
                <VoiceNoteButton url="/ai/transcribe" onResult={onVoice} testid="note-voice" label="Speak it" />
              </div>
              <textarea ref={firstRef} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder="Anything worth remembering about this customer…" rows={7} required maxLength={10000}
                data-testid="activity-modal-note-content" className={`${field} resize-y focus:border-orange-400/60`} />
            </div>
          )}

          <div>
            <label className={L}>Attach files <span className="text-slate-600 normal-case tracking-normal">(optional · drag, paste or browse)</span></label>
            <div onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()} data-testid="activity-modal-dropzone"
              className={`rounded-lg border-2 border-dashed cursor-pointer px-3 py-2.5 text-center transition-colors ${dragOver ? "border-purple-400/70 bg-purple-500/[0.06]" : "border-white/12 bg-white/[0.02] hover:border-white/25"}`}>
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="activity-modal-file-input" />
              <div className="flex items-center justify-center gap-2 text-[12px] text-slate-400"><UploadCloud className="w-4 h-4 text-slate-500" /> Drop files here or <span className="text-purple-300">browse</span> · up to 20 MB · lands in Attachments</div>
            </div>
            {files.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="activity-modal-staged-files">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-md border border-white/8 bg-white/[0.02] px-2 py-1.5 text-[12px]">
                    <Paperclip className="w-3.5 h-3.5 text-slate-500 shrink-0" /><span className="text-slate-200 truncate flex-1" title={f.name}>{f.name}</span>
                    <span className="text-[10px] text-slate-500 font-mono shrink-0">{fmtBytes(f.size)}</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-slate-500 hover:text-rose-300 shrink-0" data-testid={`activity-modal-file-remove-${i}`} title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>))}
                {uploadingFiles && <div className="flex items-center gap-2 text-[11px] text-purple-200 pt-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading… {upProgress}%</div>}
              </div>
            )}
          </div>

          {draft?.ai_drafted && (
            <div className="text-[12px] text-violet-200 bg-violet-500/10 border border-violet-500/30 rounded px-2 py-1.5" data-testid="activity-ai-draft-banner">
              AI draft from the CW staff notes — please check, edit and verify before saving. Nothing is logged until you press Save.
            </div>
          )}
          {err && <div className="text-[12px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5" data-testid="activity-modal-error">{err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-end gap-2 sticky bottom-0" style={{ backgroundColor: lightOn ? "#FFFFFF" : "#0F1420" }}>
          {err && <span className="mr-auto text-[12px] text-rose-300 truncate" data-testid="activity-modal-footer-error" title={err}>{err}</span>}
          <button type="button" onClick={onClose} disabled={saving} data-testid="activity-modal-cancel"
            className="px-3 py-2 rounded-md text-sm text-slate-300 hover:text-white hover:bg-white/5 disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={saving} data-testid="activity-modal-submit"
            className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold text-white shadow disabled:opacity-50 ${isActivity ? "bg-purple-600 hover:bg-purple-500" : "bg-orange-600 hover:bg-orange-500"}`}>
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {uploadingFiles ? "Uploading…" : saving ? "Saving…" : (isEdit ? "Update activity" : isActivity ? "Log activity" : "Save note")}
          </button>
        </div>
      </form>
      {show360 && (
        <div className="hidden md:block flex-1 min-w-0 rounded-2xl border border-white/10 bg-[#0B1220] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="activity-360-panel">
          <CRMPerson360 personIdProp={personId} mode="overlay" onClose={() => setShow360(false)} />
        </div>
      )}
    </div>
  );

  return createPortal(modal, document.body);
}
